import { type ReactNode } from 'react';
import * as stylex from '@stylexjs/stylex';
import { sentinel } from '../theme/semantic.stylex';
import { SENTINEL_DARK_LOGO_URL } from '../branding/types';

const fadeInUp = stylex.keyframes({
  from: { opacity: 0, transform: 'translateY(8px) scale(0.985)' },
  to: { opacity: 1, transform: 'translateY(0) scale(1)' },
});

const reveal = stylex.keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 },
});

const shellStyles = stylex.create({
  root: {
    minHeight: '100vh',
    minWidth: '100%',
    display: 'grid',
    gridTemplateRows: 'auto 1fr auto',
    backgroundColor: sentinel.surfaceApp,
    color: sentinel.textBody,
    fontFamily: sentinel.fontBody,
    position: 'relative',
    overflowX: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '2.5rem',
    paddingBottom: '1.25rem',
    animationName: { default: reveal, '@media (prefers-reduced-motion: reduce)': 'none' },
    animationDuration: '420ms',
    animationTimingFunction: sentinel.easeOut,
    animationFillMode: 'both',
  },
  main: {
    display: 'grid',
    placeItems: 'center',
    paddingTop: '1.5rem',
    paddingBottom: '2rem',
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    position: 'relative',
    zIndex: 1,
  },
  footer: {
    paddingTop: '1.5rem',
    paddingBottom: '2rem',
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
    display: 'flex',
    justifyContent: 'center',
    color: sentinel.textMuted,
    fontSize: '0.8125rem',
    textAlign: 'center',
  },
});

export interface AuthShellProps {
  children: ReactNode;
  footer?: ReactNode;
  brandWordmark?: ReactNode;
}

export function AuthShell({ children, footer, brandWordmark }: AuthShellProps) {
  return (
    <div {...stylex.props(shellStyles.root)}>
      <header {...stylex.props(shellStyles.header)}>
        {brandWordmark ?? <img src={SENTINEL_DARK_LOGO_URL} alt="VicOne" height={48} />}
      </header>
      <main {...stylex.props(shellStyles.main)}>{children}</main>
      <footer {...stylex.props(shellStyles.footer)}>{footer}</footer>
    </div>
  );
}

const cardStyles = stylex.create({
  root: {
    width: '100%',
    backgroundColor: sentinel.surfaceElevated,
    borderRadius: '0.875rem',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: sentinel.borderEmphasis,
    boxShadow: sentinel.shadowThick,
    padding: '2rem',
    display: 'grid',
    gap: '1.25rem',
    animationName: { default: fadeInUp, '@media (prefers-reduced-motion: reduce)': 'none' },
    animationDuration: '420ms',
    animationTimingFunction: sentinel.easeOut,
    animationFillMode: 'both',
    animationDelay: '60ms',
    color: sentinel.textBody,
  },
  narrow: { maxWidth: '440px' },
  wide: { maxWidth: '720px' },
  heading: { display: 'grid', gap: '0.375rem' },
  title: {
    margin: 0,
    fontFamily: sentinel.fontDisplay,
    fontSize: '1.5rem',
    fontWeight: 600,
    letterSpacing: '-0.02em',
    color: sentinel.textBody,
    lineHeight: 1.2,
  },
  subtitle: {
    margin: 0,
    fontFamily: sentinel.fontBody,
    fontSize: '0.9375rem',
    color: sentinel.textMuted,
    lineHeight: 1.5,
  },
});

export interface AuthCardProps {
  children: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  width?: 'narrow' | 'wide';
  /** Sets `data-testid` on the section root. e2e tests use `${testId}-title`
   *  to grab the inner h1. */
  testId?: string;
  /** Override the auto-derived h1 testid. */
  titleTestId?: string;
}

export function AuthCard({
  children,
  title,
  subtitle,
  width = 'narrow',
  testId,
  titleTestId,
}: AuthCardProps) {
  const resolvedTitleTestId = titleTestId ?? (testId ? `${testId}-title` : undefined);
  return (
    <section {...stylex.props(cardStyles.root, cardStyles[width])} data-testid={testId}>
      {title || subtitle ? (
        <header {...stylex.props(cardStyles.heading)}>
          {title ? (
            <h1 {...stylex.props(cardStyles.title)} data-testid={resolvedTitleTestId}>
              {title}
            </h1>
          ) : null}
          {subtitle ? <p {...stylex.props(cardStyles.subtitle)}>{subtitle}</p> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}
